/**
 * Keyed registry of mounted surfaces.
 *
 * Keyed rather than a single slot: a surface can unmount and remount on tab
 * switches, and one provider may back more than one surface type, so a
 * mis-slotted registration would later dispatch a command into the wrong
 * surface. Deregistration is token-guarded, so a B-mounts-before-A-unmounts
 * sequence cannot have A's cleanup clear B.
 */
import { noteRegistration } from './diagnostics';
import type { ViewSurfaceRegistration } from './types';

export interface RegistryEntry {
  /** Identity of one mount. Deregistration is a no-op unless it matches. */
  readonly token: symbol;
  registration: ViewSurfaceRegistration;
  /** Bumped whenever the INPUT side of the snapshot changes. */
  revision: number;
  /** Bumped only when the COMMITTED state changes. See `getBaseFingerprint`. */
  committedRevision: number;
  /** Last seen committed-state fingerprint, or '' for a surface with none. */
  lastBaseHash: string;
  /** The `revision` at which derived values were last seen to settle. */
  derivedRevision: number;
  lastInputHash: string;
}

const registry = new Map<string, RegistryEntry>();
let activeViewKey: string | null = null;
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((fn) => fn());
}

/** Most recently registered surviving surface, or null when empty. */
function newestViewKey(): string | null {
  return Array.from(registry.keys()).at(-1) ?? null;
}

/**
 * Register a mounted surface. Returns an unregister function that is safe to
 * call out of order — it clears the entry only if this mount still owns it.
 */
export function registerViewSurface(
  registration: ViewSurfaceRegistration
): () => void {
  const token = Symbol(registration.viewKey);
  registry.set(registration.viewKey, {
    token,
    registration,
    revision: 0,
    committedRevision: 0,
    derivedRevision: 0,
    lastInputHash: '',
    lastBaseHash: '',
  });
  activeViewKey = registration.viewKey;
  noteRegistration(registration.viewKey, registration.entityId);
  notify();

  return () => {
    const current = registry.get(registration.viewKey);
    if (current?.token !== token) {
      return;
    }
    registry.delete(registration.viewKey);
    if (activeViewKey === registration.viewKey) {
      activeViewKey = newestViewKey();
    }
    notify();
  };
}

/**
 * Replace the registration for an already-registered surface, preserving its
 * token and revision counters.
 *
 * This is how a registration always sees the latest render's state without
 * re-registering. The module registry is the mutable slot, so no ref is
 * involved — re-registering instead would reset revisions and, once an undo
 * journal exists, drop it.
 */
export function refreshRegistration(
  registration: ViewSurfaceRegistration
): void {
  const entry = registry.get(registration.viewKey);
  if (entry) {
    entry.registration = registration;
  }
}

/**
 * Internal: the entry registered under `viewKey`, active or not.
 *
 * Dispatch addresses a surface by key rather than taking whichever one is
 * active, so an action can reach a surface the user is not currently looking
 * at. Two surfaces can be mounted at once — sibling tabs of one workspace —
 * and an action that names one of them means that one, not the one that
 * happened to register last.
 */
export function entryForKey(viewKey: string): RegistryEntry | null {
  return registry.get(viewKey) ?? null;
}

export function getActiveRegistration(): ViewSurfaceRegistration | null {
  return activeEntry()?.registration ?? null;
}

/** Internal: the entry backing the active surface, counters included. */
export function activeEntry(): RegistryEntry | null {
  if (!activeViewKey) {
    return null;
  }
  return registry.get(activeViewKey) ?? null;
}

export function subscribeViewSurfaces(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

/** Test-only: drop all registrations and reset the active surface. */
export function resetViewSurfaces(): void {
  registry.clear();
  activeViewKey = null;
  subscribers.clear();
}
